import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import { Cog6ToothIcon } from "@heroicons/react/24/outline";

import StudentGrid from "../components/classroom/StudentGrid";
import StudentTaskModal from "../components/classroom/StudentTaskModal";
import NewTeacherWelcomeModal from "../components/NewTeacherWelcomeModal"; 
import TaskIcon from "../components/TaskIcon";

function getSeason(date = new Date()) {
  const month = date.getMonth();
  if (month >= 2 && month <= 4) return "spring";
  if (month >= 5 && month <= 7) return "summer";
  if (month >= 8 && month <= 10) return "autumn";
  return "winter";
}

function getNextSeasonStart(date = new Date()) {
  const seasonStartMonths = [2, 5, 8, 11];
  const nextMonth = seasonStartMonths.find((month) =>
    new Date(date.getFullYear(), month, 1).getTime() > date.getTime()
  );

  return nextMonth === undefined
    ? new Date(date.getFullYear() + 1, 2, 1)
    : new Date(date.getFullYear(), nextMonth, 1);
}

export default function ClassroomScreen() {
  const [students, setStudents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [activeStudent, setActiveStudent] = useState(null);
  const [progress, setProgress] = useState({});
  const [optionalCompleted, setOptionalCompleted] = useState({});
  const [totalTasksByStudent, setTotalTasksByStudent] = useState({});
  const [showWelcome, setShowWelcome] = useState(false);
  const navigate = useNavigate();
  const [taskListTitle, setTaskListTitle] = useState("Taken van vandaag");
  const [tasksVisibleOnHome, setTasksVisibleOnHome] = useState(true);
  const [season, setSeason] = useState(() => getSeason());
  const [classTimerEnabled, setClassTimerEnabled] = useState(false);
  const [classTimerMinutes, setClassTimerMinutes] = useState(10);
  const [classTimerStartedAt, setClassTimerStartedAt] = useState(null);
  const [classTimerPausedAt, setClassTimerPausedAt] = useState(null);
  const [clockNow, setClockNow] = useState(Date.now());

  useEffect(() => {
    const nextSeasonStart = getNextSeasonStart();
    const timer = window.setTimeout(() => setSeason(getSeason()), nextSeasonStart.getTime() - Date.now() + 1000);
    return () => window.clearTimeout(timer);
  }, [season]);


  useEffect(() => {
    loadStudents();
    loadTasks();
  }, []);

  async function loadStudents() {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) return;

    const { data } = await supabase
      .from("students")
      .select("*")
      .eq("teacher_id", user.id)
      .order("student_name", { ascending: true });

    if (data) setStudents(data.filter((student) => !student.hidden_from_class));
  }

useEffect(() => {
    async function checkTeacherPin() {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return;

      const { data: teacher } = await supabase
        .from("teachers")
        .select("pin_code")
        .eq("id", user.id)
        .single();

      // If first-time user → show welcome modal
      if (teacher?.pin_code === "0000") {
        setShowWelcome(true);
      }
    }

    checkTeacherPin();
  }, []);

  useEffect(() => {
    let active = true;

    async function loadClassroomSettings() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("teachers")
        .select("task_list_title, tasks_visible_on_home, class_timer_enabled, class_timer_duration_minutes, class_timer_started_at, class_timer_paused_at")
        .eq("id", user.id)
        .single();

      if (!active || !data) return;
      if (data.task_list_title) setTaskListTitle(data.task_list_title);
      setTasksVisibleOnHome(data.tasks_visible_on_home ?? true);
      setClassTimerEnabled(Boolean(data.class_timer_enabled));
      setClassTimerMinutes(data.class_timer_duration_minutes || 10);
      setClassTimerStartedAt(data.class_timer_started_at || null);
      setClassTimerPausedAt(data.class_timer_paused_at || null);
    }

    loadClassroomSettings();
    const poll = window.setInterval(loadClassroomSettings, 5000);
    return () => {
      active = false;
      window.clearInterval(poll);
    };
  }, []);

  useEffect(() => {
    if (!classTimerEnabled || classTimerPausedAt) return undefined;
    const interval = window.setInterval(() => setClockNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [classTimerEnabled, classTimerPausedAt]);


  async function loadTasks() {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) return;

    const { data } = await supabase
      .from("tasks")
      .select("*")
      .eq("teacher_id", user.id)
      .order("created_at", { ascending: true });

    if (data) setTasks(data);
  }

  function openTeacherDashboard() {
    navigate("/pin");
  }

  function openStudentTasks(student) {
    setActiveStudent(student);
  }

const loadProgress = useCallback(async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return;

  if (students.length === 0) {
    setProgress({});
    setOptionalCompleted({});
    setTotalTasksByStudent({});
    return;
  }

  const taskById = new Map(tasks.map((task) => [task.id, task]));
  const requiredTaskIds = new Set(
    tasks.filter((task) => task.priority !== "optional").map((task) => task.id)
  );
  const allTaskIds = new Set(
    tasks.filter((task) => task.audience !== "targeted").map((task) => task.id)
  );

  const { data: groupRows } = await supabase
    .from("groups")
    .select("id")
    .eq("teacher_id", user.id);

  const groupIds = (groupRows || []).map((row) => row.id);

  let groupStudents = [];
  if (groupIds.length > 0) {
    const { data } = await supabase
      .from("group_students")
      .select("group_id, student_id")
      .in("group_id", groupIds);
    groupStudents = data || [];
  }

  const studentGroupsMap = new Map();
  groupStudents.forEach((row) => {
    if (!studentGroupsMap.has(row.student_id)) {
      studentGroupsMap.set(row.student_id, new Set());
    }
    studentGroupsMap.get(row.student_id).add(row.group_id);
  });

  let assignmentRows = [];
  if (tasks.length > 0) {
    const taskIds = tasks.map((task) => task.id);
    const { data } = await supabase
      .from("task_assignments")
      .select("task_id, group_id")
      .in("task_id", taskIds);
    assignmentRows = data || [];
  }

  const assignmentsByGroup = new Map();

  assignmentRows.forEach((row) => {
    if (row.group_id) {
      if (!assignmentsByGroup.has(row.group_id)) {
        assignmentsByGroup.set(row.group_id, new Set());
      }
      assignmentsByGroup.get(row.group_id).add(row.task_id);
    }
  });

  const visibleTasksByStudent = new Map();
  const totalTasksMap = {};

  students.forEach((student) => {
    const visible = new Set(allTaskIds);

    const studentGroups = studentGroupsMap.get(student.id);
    if (studentGroups) {
      studentGroups.forEach((groupId) => {
        const groupTasks = assignmentsByGroup.get(groupId);
        if (!groupTasks) return;
        groupTasks.forEach((taskId) => {
          if (taskById.get(taskId)?.audience === "targeted") {
            visible.add(taskId);
          }
        });
      });
    }

    let requiredCount = 0;
    visible.forEach((taskId) => {
      if (requiredTaskIds.has(taskId)) requiredCount++;
    });

    visibleTasksByStudent.set(student.id, visible);
    totalTasksMap[student.id] = requiredCount;
  });

  const studentIds = students.map((student) => student.id);
  const taskIds = tasks.map((task) => task.id);

  let statusRows = [];
  if (studentIds.length > 0 && taskIds.length > 0) {
    const { data } = await supabase
      .from("task_status")
      .select("student_id, task_id, completed")
      .in("student_id", studentIds)
      .in("task_id", taskIds);
    statusRows = data || [];
  }

  const progressMap = {}; // studentId → completed required count
  const optionalMap = {}; // studentId → completed optional task

  statusRows.forEach((row) => {
    if (!row.completed) return;

    const visible = visibleTasksByStudent.get(row.student_id);
    if (!visible || !visible.has(row.task_id)) return;

    const task = taskById.get(row.task_id);
    if (!task) return;

    if (task.priority === "optional") {
      optionalMap[row.student_id] = true;
    } else {
      progressMap[row.student_id] = (progressMap[row.student_id] || 0) + 1;
    }
  });

  setProgress(progressMap);
  setOptionalCompleted(optionalMap);
  setTotalTasksByStudent(totalTasksMap);
}, [students, tasks]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

function closeModal() {
  setActiveStudent(null);
  loadProgress(); // refresh progress after changes
}


  
  const classTasks = tasks.filter((task) => task.audience !== "targeted");
  const classRequiredTotal = Object.values(totalTasksByStudent).reduce(
    (total, count) => total + count,
    0
  );
  const classRequiredCompleted = Object.entries(progress).reduce(
    (total, [studentId, count]) =>
      total + Math.min(count, totalTasksByStudent[studentId] || 0),
    0
  );
  const classProgressPercent = classRequiredTotal > 0
    ? Math.round((classRequiredCompleted / classRequiredTotal) * 100)
    : 0;
  const timerReferenceTime = classTimerPausedAt ? Date.parse(classTimerPausedAt) : clockNow;
  const timerElapsedSeconds = classTimerStartedAt
    ? Math.floor((timerReferenceTime - Date.parse(classTimerStartedAt)) / 1000)
    : 0;
  const timerDurationSeconds = classTimerMinutes * 60;
  const timerRemainingSeconds = Math.min(
    timerDurationSeconds,
    Math.max(0, timerDurationSeconds - timerElapsedSeconds)
  );
  const timerDisplay = `${String(Math.floor(timerRemainingSeconds / 60)).padStart(2, "0")}:${String(timerRemainingSeconds % 60).padStart(2, "0")}`;

  return (
  <div
    className={`ct-page ct-classroom-page season-${season}`}
    style={{ "--season-background": `url("${process.env.PUBLIC_URL}/seasons/${season}.webp")` }}
  >
    <header className="ct-classroom-header">
      <div>
        <h1 className="ct-page-title">Samen zelfstandig aan de slag</h1>
      </div>
      <button
        onClick={openTeacherDashboard}
        className="ct-gear-button"
        title="Leerkracht dashboard"
        aria-label="Leerkracht dashboard"
      >
        <Cog6ToothIcon className="h-6 w-6" />
      </button>
    </header>

    <div className={`ct-classroom-layout${tasksVisibleOnHome ? "" : " is-full-width"}`}>
      <section className={`ct-surface ct-students-surface${tasksVisibleOnHome ? " is-compact" : " is-expanded"}`}>
        <div className="ct-panel-heading">
          <div>
            <h2 className="ct-panel-title">Leerlingen</h2>
            <p className="ct-panel-subtitle">Tik op een naam om de taken te openen.</p>
          </div>
          <span className="ct-class-summary" title="Voortgang van alle moetjes">
            <span className="ct-badge ct-student-count-badge">{students.length} leerlingen</span>
            <span className="ct-class-progress-track" aria-hidden="true">
              <span
                className="ct-class-progress-fill"
                style={{ width: `${classProgressPercent}%` }}
              />
            </span>
            {classTimerEnabled && (
              <span
                className={`ct-class-timer${timerRemainingSeconds === 0 ? " is-expired" : ""}${classTimerPausedAt ? " is-paused" : ""}`}
                role="timer"
                aria-label={`${classTimerPausedAt ? "Timer gepauzeerd" : "Tijd resterend"}: ${timerDisplay}`}
              >
                <span aria-hidden="true">{classTimerPausedAt ? "⏸" : "⏱"}</span> {timerDisplay}
              </span>
            )}
          </span>
        </div>
        <StudentGrid
          students={students}
          onSelect={openStudentTasks}
          progress={progress}
          totalTasksByStudent={totalTasksByStudent}
          optionalCompleted={optionalCompleted}
          showTasks={tasksVisibleOnHome}
        />
      </section>

      {tasksVisibleOnHome && (
        <section className="ct-surface ct-task-panel">
          <div className="ct-panel-heading">
            <div>
              <h2 className="ct-panel-title">{taskListTitle}</h2>
            </div>
          </div>

          {classTasks.length === 0 ? (
            <p className="ct-panel-subtitle">Er zijn nog geen taken toegevoegd.</p>
          ) : (
            <ul className="ct-task-list">
              {[...classTasks]
                .sort((a, b) => (a.priority === "optional") - (b.priority === "optional"))
                .map((task) => (
                  <li
                    key={task.id}
                    className={`ct-task-row${task.priority === "optional" ? " is-optional" : ""}`}
                  >
                    <span className="ct-task-icon" aria-hidden="true"><TaskIcon value={task.icon} className="ct-task-svg" /></span>
                    <span className="ct-task-title">{task.title}</span>
                    <span className="ct-task-meta">
                      {task.samenwerken && <span title="Samenwerken">🤝</span>}
                      {task.zelf_nakijken && <span title="Zelf nakijken">🔑</span>}
                      {task.feedback_enabled && <span title="Feedback gevraagd">💬</span>}
                      {task.priority === "optional" && (
                        <span className="ct-badge is-optional">Magje</span>
                      )}
                    </span>
                  </li>
                ))}
            </ul>
          )}
        </section>
      )}
    </div>

{showWelcome && (
  <NewTeacherWelcomeModal onClose={() => setShowWelcome(false)} />
)}

    {/* Student Task Modal */}
    {activeStudent && (
      <StudentTaskModal
        student={activeStudent}
        onClose={closeModal}
      />
    )}
  </div>
);

}
